import Box from "@mui/material/Box";
import { gr } from "src/design-system/tokens";
import GrText from "src/design-system/components/GrText";
import GrTextField from "src/design-system/components/GrTextField";
import GrChoiceChips from "src/design-system/components/GrChoiceChips";
import GrNumberStepper from "src/design-system/components/GrNumberStepper";
import GrDurationField from "src/design-system/components/GrDurationField";
import PublicTrackerField from "./public/PublicTrackerField";

/**
 * Member check-in form pieces: a general question block (text, answer chips,
 * long text) and a measurement row (last time, min/sec or − / + input, and
 * how it compares with last time as the member types).
 */

export const isEmptyAnswer = (value) =>
  value == null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

/** A question's answer on a submission row (by id, then by label). */
export const answerOnRow = (row, question) => {
  const answers = Array.isArray(row?.answers) ? row.answers : [];
  const id = String(question?.id || "");
  const label = String(question?.label || "").trim().toLowerCase();
  const hit =
    answers.find((a) => id && String(a?.questionId || "") === id) ||
    answers.find(
      (a) => label && String(a?.label || "").trim().toLowerCase() === label
    );
  return hit && !isEmptyAnswer(hit.value) ? hit.value : null;
};

export const unitOf = (question) =>
  question?.defaultUnit ||
  (Array.isArray(question?.units) ? question.units[0] : "") ||
  "";

/** time | count | number — how a measurement is typed in. */
export const measureKind = (question) => {
  const type = String(question?.measureType || "").toLowerCase();
  const unit = unitOf(question).toLowerCase();
  if (type === "time") return "time";
  if (type === "reps" || type === "score" || ["reps", "count", "pts", "score"].includes(unit)) {
    return "count";
  }
  return "number";
};

// ─── time values ────────────────────────────────────────────────────────────
// Stored in the question's unit like before: seconds for "sec", "h:mm:ss" for
// "hh:mm:ss", otherwise minutes (4:30 → 4.5).

const toSeconds = (raw, unit) => {
  if (isEmptyAnswer(raw)) return null;
  const text = String(raw).trim();
  const clock = text.match(/^(\d{1,3}):(\d{2})(?::(\d{2}))?$/);
  if (clock) {
    const [, a, b, c] = clock;
    return c != null
      ? Number(a) * 3600 + Number(b) * 60 + Number(c)
      : Number(a) * 60 + Number(b);
  }
  const n = Number(text.replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return unit === "sec" ? n : n * 60;
};

const fromSeconds = (secs, unit) => {
  const total = Math.max(0, Math.round(secs));
  if (unit === "sec") return String(total);
  if (unit === "hh:mm:ss") {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return String(Math.round((total / 60) * 100) / 100);
};

export const formatClock = (secs) => {
  const total = Math.max(0, Math.round(secs));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

const toNumber = (raw) => {
  if (isEmptyAnswer(raw)) return null;
  const n = Number(String(raw).replace(",", ".").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

/** Comparable number (seconds for time) for a stored answer. */
export const measureValue = (raw, question) =>
  measureKind(question) === "time"
    ? toSeconds(raw, unitOf(question))
    : toNumber(raw);

/** "4:30 min", "40 reps", "82 kg" — a stored answer for display. */
export const formatMeasure = (raw, question) => {
  if (isEmptyAnswer(raw)) return "";
  if (measureKind(question) === "time") {
    const secs = toSeconds(raw, unitOf(question));
    if (secs == null) return String(raw);
    return secs >= 3600 ? formatClock(secs) : `${formatClock(secs)} min`;
  }
  const unit = unitOf(question);
  const text = String(raw);
  // "6/10", "80%": no space before a scale or percent unit.
  const gap = /^[/%]/.test(unit) ? "" : " ";
  return unit && /^-?\d+([.,]\d+)?$/.test(text.trim()) ? `${text}${gap}${unit}` : text;
};

/**
 * Note vs last time → { text, tone } (good / warn / same), or null when there
 * is nothing to compare.
 */
const progressNote = (raw, lastRaw, question) => {
  const now = measureValue(raw, question);
  const last = measureValue(lastRaw, question);
  if (now == null || last == null) return null;
  if (now === last) return { text: "Same as last time", tone: "same" };
  // ascending = less is better (run time, weight loss); descending = more.
  const lessIsBetter = String(question?.measureDirection || "") === "ascending";
  const better = lessIsBetter ? now < last : now > last;
  const tone = better ? "good" : "warn";
  if (measureKind(question) === "time") {
    const amount = formatClock(Math.abs(now - last));
    if (better) {
      return { text: `Better than last time · ${amount} ${lessIsBetter ? "faster" : "longer"}`, tone };
    }
    return { text: `${lessIsBetter ? "Slower" : "Shorter"} than last time · ${amount}`, tone };
  }
  const unit = unitOf(question);
  const amount = `${now > last ? "+" : "−"}${Math.round(Math.abs(now - last) * 100) / 100}${unit ? ` ${unit}` : ""}`;
  if (better) return { text: `Better than last time · ${amount}`, tone };
  return { text: `${now < last ? "Lower" : "Higher"} than last time · ${amount}`, tone };
};

const NOTE_COLORS = { good: gr.color.ok, warn: gr.color.late, same: gr.color.muted };

// ─── layout ────────────────────────────────────────────────────────────────

const labelSx = {
  display: "block",
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.ink,
};
const starSx = { color: gr.color.danger, ml: gr.space[1] };
const errorSx = { mt: gr.space[1], color: gr.color.danger };
const measureRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(160px, 200px)" },
  gap: { xs: gr.space[3], sm: gr.space[6] },
  alignItems: "start",
};
const noteSx = (tone) => ({
  mt: gr.space[1],
  textAlign: "right",
  fontWeight: gr.font.weight.semibold,
  color: NOTE_COLORS[tone],
});

const QuestionLabel = ({ question, htmlFor, fallback }) => (
  <Box component="label" htmlFor={htmlFor} sx={labelSx}>
    {question.label || question.statement || fallback}
    {question.required ? (
      <Box component="span" sx={starSx}>
        *
      </Box>
    ) : null}
  </Box>
);

const ErrorLine = ({ error }) =>
  error ? (
    <GrText variant="caption" sx={errorSx}>
      {error}
    </GrText>
  ) : null;

const YES_NO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

/** General (non-measurement) question: label, then its input. */
export const GeneralQuestion = ({ question, value, error, onChange }) => {
  const type = question.fieldType || "short_text";
  const options = Array.isArray(question.options) ? question.options.map(String) : [];
  const inputId = `member-input-${question.id}`;
  const label = question.label || question.statement || "Question";
  let control;
  let ownError = true;
  if (type === "yes_no") {
    control = (
      <GrChoiceChips ariaLabel={label} options={YES_NO} value={value} onChange={onChange} error={Boolean(error)} />
    );
  } else if (["multiple_choice", "dropdown", "status"].includes(type) && options.length) {
    control = (
      <GrChoiceChips ariaLabel={label} options={options} value={value} onChange={onChange} error={Boolean(error)} />
    );
  } else if (type === "checkbox" && options.length) {
    control = (
      <GrChoiceChips
        ariaLabel={label}
        options={options}
        value={value}
        onChange={onChange}
        multiple
        error={Boolean(error)}
      />
    );
  } else if (type === "scale" && options.length && options.length <= 10) {
    control = (
      <GrChoiceChips ariaLabel={label} options={options} value={value} onChange={onChange} error={Boolean(error)} />
    );
  } else if (type === "short_text" || type === "long_text") {
    control = (
      <GrTextField
        id={inputId}
        fullWidth
        multiline={type === "long_text"}
        minRows={type === "long_text" ? 3 : undefined}
        placeholder={question.placeholder || "Type your answer…"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        error={Boolean(error)}
      />
    );
  } else {
    // Dates, uploads, scales without options…: the existing field shows its own error.
    ownError = false;
    control = (
      <PublicTrackerField
        question={question}
        value={value}
        error={error}
        onChange={onChange}
        surveyMode
        hideLabel
        portalFlat
        inputId={inputId}
      />
    );
  }
  return (
    <Box id={`member-q-${question.id}`}>
      <QuestionLabel question={question} htmlFor={inputId} fallback="Question" />
      {question.helpText ? (
        <GrText variant="secondary" sx={{ mt: gr.space[1] }}>
          {question.helpText}
        </GrText>
      ) : null}
      <Box sx={{ mt: gr.space[3] }}>{control}</Box>
      {ownError ? <ErrorLine error={error} /> : null}
    </Box>
  );
};

/** One measurement: what it is + last time (left), input + note (right). */
export const MeasureQuestion = ({ question, value, error, onChange, lastValue }) => {
  const kind = measureKind(question);
  const unit = unitOf(question);
  const inputId = `member-input-${question.id}`;
  const label = question.label || question.statement || "Measurement";
  const note = progressNote(value, lastValue, question);
  const numeric = question.fieldType === "number" || !question.fieldType;
  let control;
  if (!numeric) {
    control = (
      <PublicTrackerField
        question={question}
        value={value}
        error={error}
        onChange={onChange}
        surveyMode
        hideLabel
        portalFlat
        inputId={inputId}
      />
    );
  } else if (kind === "time") {
    control = (
      <GrDurationField
        id={inputId}
        ariaLabel={label}
        value={toSeconds(value, unit)}
        onChange={(secs) => onChange(secs == null ? "" : fromSeconds(secs, unit))}
        error={Boolean(error)}
      />
    );
  } else {
    control = (
      <GrNumberStepper
        id={inputId}
        ariaLabel={label}
        unit={unit}
        decimals={kind !== "count"}
        value={value}
        onChange={onChange}
        error={Boolean(error)}
      />
    );
  }
  return (
    <Box id={`member-q-${question.id}`} sx={measureRowSx}>
      <Box sx={{ minWidth: 0 }}>
        <QuestionLabel question={question} htmlFor={inputId} fallback="Measurement" />
        {question.helpText ? (
          <GrText variant="secondary" sx={{ mt: gr.space[1] }}>
            {question.helpText}
          </GrText>
        ) : null}
        {lastValue != null ? (
          <GrText variant="caption" sx={{ mt: gr.space[1] }}>
            Last time{" "}
            <Box component="span" sx={{ color: gr.color.ink, fontWeight: gr.font.weight.semibold }}>
              {formatMeasure(lastValue, question)}
            </Box>
          </GrText>
        ) : null}
      </Box>
      <Box>
        {control}
        {note ? (
          <GrText variant="caption" sx={noteSx(note.tone)}>
            {note.text}
          </GrText>
        ) : null}
        {numeric ? <ErrorLine error={error} /> : null}
      </Box>
    </Box>
  );
};
